import { UploadOutlined } from '@ant-design/icons';
import { Button, Collapse, FormInstance, Input, message, Spin, Typography, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useSaveCandidateMutation from '../../hooks/useSaveCandidateMutation';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams, { Optional } from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { CandidateDetail, CandidateId, JobPostingId } from '../../types';
import styles from './EditCandidateAttachmentPage.module.css';

const mimeTypes = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

const supportedFiles = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.png',
  '.jpeg',
  '.webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/png',
  'image/jpeg',
  'image/webp',
];

const maxFileSizeInBytes = 2_097_152; // 2Mb

interface EditCandidateFormValues {
  id: CandidateDetail['id'];
  attachment?: UploadFile[];
  physicalDisabilityReport?: UploadFile[];
  hearingDisabilityReport?: UploadFile[];
  visualDisabilityReport?: UploadFile[];
  mentalDisabilityReport?: UploadFile[];
  intellectualDisabilityReport?: UploadFile[];
  otherDisabilityReport?: UploadFile[];
}

const EditCandidateAttachmentPage = () => {
  const { tenant, id, applicant_id } = useTypedParams({
    tenant: String,
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });
  const [form] = Form.useForm();
  const attachmentValue = Form.useWatch('attachment', form);
  const physicalDisabilityReportValue = Form.useWatch('physicalDisabilityReport', form);
  const hearingDisabilityReportValue = Form.useWatch('hearingDisabilityReport', form);
  const visualDisabilityReportValue = Form.useWatch('visualDisabilityReport', form);
  const mentalDisabilityReportValue = Form.useWatch('mentalDisabilityReport', form);
  const intellectualDisabilityReportValue = Form.useWatch('intellectualDisabilityReport', form);
  const otherDisabilityReportValue = Form.useWatch('otherDisabilityReport', form);
  const queryClient = useQueryClient();
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();
  const [setIsDirty] = useDiscardChangesModal(form);
  const saveCandidateMutation = useSaveCandidateMutation();
  const { setDrawerForm: setForm, setDrawerMutationIsLoading: setMutationIsLoading } =
    useOutletContext<{
      setDrawerForm: React.Dispatch<React.SetStateAction<FormInstance | undefined>>;
      setDrawerMutationIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    }>();

  useEffect(() => {
    setForm(form);
  }, [form, setForm]);

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const attachment = candidate.data?.attachment ?? null;

  const candidateDisabilities = useAPIQuery([
    '/candidate-disability/',
    {
      params: {
        candidate: applicant_id,
      },
    },
  ]);

  const physicalDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'PHYSICAL'
  )?.report;
  const hearingDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'HEARING'
  )?.report;
  const visualDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'VISUAL'
  )?.report;
  const mentalDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'MENTAL'
  )?.report;
  const intellectualDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'INTELLECTUAL'
  )?.report;
  const otherDisabilityReportInitialValue = candidateDisabilities.data?.results.find(
    (disability) => disability.type === 'OTHER'
  )?.report;

  const isPCD = !!(candidateDisabilities.data?.count && candidateDisabilities.data?.count > 0);

  const mutation = useAPIMutation(
    async ({ id, attachment }: EditCandidateFormValues, api) => {
      if (attachment?.length) {
        if (!attachment[0].url) {
          await saveCandidateMutation.mutateAsync({
            id,
            attachment: attachment[0].originFileObj,
          });
        }
      } else {
        await saveCandidateMutation.mutateAsync({ id, attachment: null });
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate/');
      },
      onSuccess: () => {
        setIsDirty(false);
        message.success('Candidato atualizado!');
        if (id) {
          navigate({
            pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/attachments/',
            params: { tenant, id, applicant_id },
            search: searchParams,
          });
        } else {
          navigate({
            pathname: '/:tenant/talent-pool/candidates/:applicant_id/attachments/',
            params: { tenant, applicant_id },
            search: searchParams,
          });
        }
      },
    }
  );

  const candidateDisabilityReportsMutation = useAPIMutation(
    async (
      {
        id,
        physicalDisabilityReport,
        hearingDisabilityReport,
        visualDisabilityReport,
        mentalDisabilityReport,
        intellectualDisabilityReport,
        otherDisabilityReport,
      }: Omit<EditCandidateFormValues, 'attachment'>,
      api
    ) => {
      const disabilityReportDict = {
        PHYSICAL: physicalDisabilityReport,
        HEARING: hearingDisabilityReport,
        VISUAL: visualDisabilityReport,
        MENTAL: mentalDisabilityReport,
        INTELLECTUAL: intellectualDisabilityReport,
        OTHER: otherDisabilityReport,
      };

      const candidateDisabilities = await api.get(`/candidate-disability/`, {
        params: { candidate: id },
      });

      for (const candidateDisability of candidateDisabilities.data.results) {
        const candidateDisabilityID = candidateDisability.id;
        const disabilityReport = disabilityReportDict[candidateDisability.type];

        const formData = new FormData();

        if (disabilityReport?.length) {
          if (!disabilityReport[0].url) {
            formData.append('report', disabilityReport[0].originFileObj ?? '');
            await api.patch(`/candidate-disability/${candidateDisabilityID}/`, formData, {
              maxBodyLength: maxFileSizeInBytes,
            });
          }
        } else {
          formData.append('report', '');
          await api.patch(`/candidate-disability/${candidateDisabilityID}/`, formData, {
            maxBodyLength: maxFileSizeInBytes,
          });
        }
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-disability/');
      },
    }
  );

  useEffect(() => {
    setMutationIsLoading(mutation.isLoading);
  }, [mutation.isLoading, setMutationIsLoading]);

  return (
    <ErrorResult error={candidate.error}>
      <Spin spinning={candidate.isLoading}>
        <Helmet>
          <title>Editar Anexos | {candidate.data?.name || '...'} | Recrutamento e Seleção</title>
        </Helmet>
        <Form<EditCandidateFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            id: candidate.data?.id,
            attachment: attachment
              ? [
                  {
                    name: attachment.name,
                    status: 'done',
                    url: attachment.url,
                    type: attachment.mimetype,
                  },
                ]
              : [],
            physicalDisabilityReport: physicalDisabilityReportInitialValue
              ? [
                  {
                    name: physicalDisabilityReportInitialValue.name,
                    status: 'done',
                    url: physicalDisabilityReportInitialValue.url,
                    type: physicalDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
            hearingDisabilityReport: hearingDisabilityReportInitialValue
              ? [
                  {
                    name: hearingDisabilityReportInitialValue.name,
                    status: 'done',
                    url: hearingDisabilityReportInitialValue.url,
                    type: hearingDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
            visualDisabilityReport: visualDisabilityReportInitialValue
              ? [
                  {
                    name: visualDisabilityReportInitialValue.name,
                    status: 'done',
                    url: visualDisabilityReportInitialValue.url,
                    type: visualDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
            mentalDisabilityReport: mentalDisabilityReportInitialValue
              ? [
                  {
                    name: mentalDisabilityReportInitialValue.name,
                    status: 'done',
                    url: mentalDisabilityReportInitialValue.url,
                    type: mentalDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
            intellectualDisabilityReport: intellectualDisabilityReportInitialValue
              ? [
                  {
                    name: intellectualDisabilityReportInitialValue.name,
                    status: 'done',
                    url: intellectualDisabilityReportInitialValue.url,
                    type: intellectualDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
            otherDisabilityReport: otherDisabilityReportInitialValue
              ? [
                  {
                    name: otherDisabilityReportInitialValue.name,
                    status: 'done',
                    url: otherDisabilityReportInitialValue.url,
                    type: otherDisabilityReportInitialValue.mimetype,
                  },
                ]
              : [],
          }}
          onFinish={(values) => {
            mutation.mutate(values);
            candidateDisabilityReportsMutation.mutate(values);
          }}
          onValuesChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Collapse
            ghost
            className={styles.collapse}
            expandIconPosition="end"
            defaultActiveKey={['resume', 'pcd_reports']}
          >
            <Collapse.Panel
              header={<Typography.Title level={3}>Currículo</Typography.Title>}
              key="resume"
            >
              <Form.Row>
                <Form.Col span={24}>
                  <Form.Item
                    name="attachment"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) {
                        return e;
                      }
                      return e && e.fileList;
                    }}
                  >
                    <Upload
                      maxCount={1}
                      listType="text"
                      accept={supportedFiles.join(',')}
                      beforeUpload={(file: UploadFile) => {
                        if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                          if (!file.size) {
                            //O que significa não ter size?
                            message.error('Erro com o arquivo.');
                            return Upload.LIST_IGNORE;
                          }
                          if (file.size >= maxFileSizeInBytes) {
                            message.error(
                              'Por favor, selecione um arquivo de até 2Mb para upload.'
                            );
                            return Upload.LIST_IGNORE;
                          }

                          return false;
                        } else {
                          message.error(
                            'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                          );
                          return Upload.LIST_IGNORE;
                        }
                      }}
                      data-testid="candidate-upload"
                    >
                      <Button disabled={attachmentValue?.length >= 1} icon={<UploadOutlined />}>
                        Upload
                      </Button>
                    </Upload>
                  </Form.Item>
                </Form.Col>
              </Form.Row>
            </Collapse.Panel>
            {isPCD && (
              <Collapse.Panel
                header={<Typography.Title level={3}>Laudos médicos</Typography.Title>}
                key="pcd_reports"
              >
                <Form.Row>
                  <Form.Col span={24} hidden={!candidate.data?.disabilities?.includes('PHYSICAL')}>
                    <Form.Item
                      label="Deficiência Física"
                      name="physicalDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={physicalDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={24} hidden={!candidate.data?.disabilities?.includes('HEARING')}>
                    <Form.Item
                      label="Deficiência Auditiva"
                      name="hearingDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={hearingDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={24} hidden={!candidate.data?.disabilities?.includes('VISUAL')}>
                    <Form.Item
                      label="Deficiência Visual"
                      name="visualDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={visualDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={24} hidden={!candidate.data?.disabilities?.includes('MENTAL')}>
                    <Form.Item
                      label="Deficiência Mental"
                      name="mentalDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={mentalDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col
                    span={24}
                    hidden={!candidate.data?.disabilities?.includes('INTELLECTUAL')}
                  >
                    <Form.Item
                      label="Deficiência Intelectual"
                      name="intellectualDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={intellectualDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={24} hidden={!candidate.data?.disabilities?.includes('OTHER')}>
                    <Form.Item
                      label="Outra Deficiência"
                      name="otherDisabilityReport"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) {
                          return e;
                        }
                        return e && e.fileList;
                      }}
                    >
                      <Upload
                        maxCount={1}
                        listType="text"
                        accept={supportedFiles.join(',')}
                        beforeUpload={(file: UploadFile) => {
                          if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                            if (!file.size) {
                              //O que significa não ter size?
                              message.error('Erro com o arquivo.');
                              return Upload.LIST_IGNORE;
                            }
                            if (file.size >= maxFileSizeInBytes) {
                              message.error(
                                'Por favor, selecione um arquivo de até 2Mb para upload.'
                              );
                              return Upload.LIST_IGNORE;
                            }

                            return false;
                          } else {
                            message.error(
                              'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                            );
                            return Upload.LIST_IGNORE;
                          }
                        }}
                        data-testid="candidate-upload"
                      >
                        <Button
                          disabled={otherDisabilityReportValue?.length >= 1}
                          icon={<UploadOutlined />}
                        >
                          Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Form.Col>
                </Form.Row>
              </Collapse.Panel>
            )}
          </Collapse>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(EditCandidateAttachmentPage);
